<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import capitalize from 'lodash/capitalize'
import ProductInformationTab from './tabs/product-information-tab.vue'
import ProductRolesTab from './tabs/product-roles-tab.vue'
import ProductVersionsTab from './tabs/product-versions-tab.vue'

import CreateRoleModal from './modals/create-role-modal.vue'
import ProductModal from './modals/product-modal.vue'
import CreateVersionModal from './modals/create-version-modal.vue'
import DeleteProductModal from './modals/delete-product-modal'

export default {
  name: 'ProductDetail',
  components: {
    ProductInformationTab,
    ProductRolesTab,
    ProductVersionsTab,
    CreateRoleModal,
    ProductModal,
    CreateVersionModal,
    DeleteProductModal,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      activeTab: '',
      loading: true,
      modal: {
        createRole: false,
        editProduct: false,
        deleteProduct: false,
        createVersion: false,
      },
    }
  },
  computed: {
    ...mapState('product', ['product']),
    ...mapGetters('me', [
      'canCreateRole',
      'showRolesList',
      'canCreateProductVersion',
      'showProductVersionsList',
      'canUpdateProduct',
      'canDeleteProduct',
    ]),
    tabs() {
      const tabsList = [
        {
          label: 'Product Information',
          name: 'information',
        },
      ]

      if (this.showRolesList) {
        tabsList.push({
          label: 'Roles',
          name: 'roles',
        })
      }

      if (this.showProductVersionsList) {
        tabsList.push({
          label: 'Versions',
          name: 'versions',
        })
      }

      return tabsList
    },
    commands() {
      return [
        this.canCreateRole && {
          command: 'createRole',
          label: 'Create Role',
        },
        this.canUpdateProduct && {
          command: 'editProduct',
          label: 'Edit Product',
        },
        this.canDeleteProduct && {
          command: 'deleteProduct',
          label: 'Delete Product',
        },
        this.canCreateProductVersion && {
          command: 'createVersion',
          label: 'Create Version',
        },
      ]
    },
  },
  async created() {
    this.loading = true
    this.activeTab = this.$route.params.tab || this.tabs[0].name
    this.fetchProduct()
  },
  methods: {
    capitalize,
    ...mapActions('product', ['getProduct']),
    ...mapActions('products', ['getProducts']),
    async fetchProduct() {
      this.loading = true
      const [error] = await this.getProduct(this.id)
      if (error) {
        this.$router.replace({ name: 'product' })
        console.error(error)
      }
      this.loading = false
    },
    onTabClick(newTab, prevTab) {
      if (newTab !== prevTab) {
        this.$router.push({
          name: 'product-details',
          params: {
            id: this.id,
            tab: newTab,
          },
        })
      }
    },
    async onCloseModal(modalName) {
      this.modal[modalName] = false

      await this.$nextTick()

      this.fetchProduct()

      if (modalName === 'editProduct') {
        this.getProducts()
      }
    },
    handleCommand(command) {
      this.modal[command] = true
    },
  },
}
</script>

<template>
  <main-layout
    :title="product.name"
    :back="{ name: 'products' }"
    :loading="loading"
  >
    <template #header>
      <el-dropdown
        trigger="click"
        @command="handleCommand"
      >
        <dropdown-icon />
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item
            v-for="(command, index) in commands"
            :key="index"
            :command="command.command"
          >
            {{ command.label }}
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>

      <create-role-modal
        v-if="modal.createRole"
        :product-id="product.id"
        :product-name="product.name"
        :roles="product.roles"
        :visible.sync="modal.createRole"
        @close-modal="onCloseModal"
      />
      <product-modal
        v-if="modal.editProduct"
        :product="product"
        :edit="true"
        :visible.sync="modal.editProduct"
        @close-modal="onCloseModal"
      />
      <delete-product-modal
        v-if="modal.deleteProduct"
        :product="product"
        :visible.sync="modal.deleteProduct"
      />
      <create-version-modal
        v-if="modal.createVersion"
        :product="product"
        :visible.sync="modal.createVersion"
        @close-modal="onCloseModal"
      />
    </template>

    <el-tabs
      v-model="activeTab"
      :before-leave="onTabClick"
    >
      <el-tab-pane
        v-for="tab in tabs"
        :key="tab.name"
        :label="tab.label"
        :name="tab.name"
        :loading="loading"
        lazy
      >
        <component
          :is="`Product${capitalize(tab.name)}Tab`"
          v-if="activeTab === tab.name"
          :ref="`${tab.name}Tab`"
          :product="product"
        />
      </el-tab-pane>
    </el-tabs>
  </main-layout>
</template>
